/**
 * Conversation history service — calls query_router_service endpoints
 * for listing, resuming, archiving, renaming and deleting conversations.
 *
 * Reuses the same axios pattern as queryService.ts.
 */

import axios from 'axios';

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type { ChatMessageFile } from '@/types/chatFiles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversationSummary {
  id: string;
  title: string | null;
  model_id: string | null;
  message_count: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model_used: string | null;
  sequence: number;
  created_at: string;
  /** Files sent with the message (AW-122 U4, `conversation_message_files`); absent before U4. */
  files?: ChatMessageFile[];
  /** Files this (assistant) message made (XC-DOCS-FROM-CHAT, `conversation_message_made_files`). */
  made_file_ids?: string[];
}

export interface ConversationDetail extends ConversationSummary {
  messages: ConversationMessage[];
}

/** The receipt of a Delete (XC-RETENTION-LIMIT): counts only, never text. */
export interface ConversationDeleted {
  conversation_id: string;
  deleted_at: string;
  messages: number;
  answer_copies: number;
  /** Query logs past the Delete's inline bound, dropped by the hourly sweep (0 for a chat of up to 1,000 turns). */
  answer_copies_pending: number;
  /** His files kept only in this chat: not deleted, they stay his (Documents lists them to him). */
  kept_files: number;
  /** Files the chat made for him (built, removed from their card or not): not deleted, listed with the kept ones. */
  made_files: number;
  /** Its copied golden questions and harness results, removed with it. */
  test_copies: number;
}

/** What a Delete leaves him: files he kept in the chat, and files it made for him. */
export interface FilesThatStay {
  kept: number;
  made: number;
}

export interface PaginatedConversations {
  items: ConversationSummary[];
  total: number;
  page: number;
  page_size: number;
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const conversationApi = axios.create({
  baseURL: config.QUERY_ROUTER_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

conversationApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function listConversations(
  page = 1,
  pageSize = 50,
  includeArchived = false,
): Promise<PaginatedConversations> {
  const { data } = await conversationApi.get<PaginatedConversations>(
    '/conversations',
    { params: { page, page_size: pageSize, ...(includeArchived && { include_archived: true }) } },
  );
  return data;
}

export async function getConversation(
  id: string,
  signal?: AbortSignal,
): Promise<ConversationDetail> {
  const { data } = await conversationApi.get<ConversationDetail>(
    `/conversations/${id}`,
    { signal },
  );
  return data;
}

export async function archiveConversation(
  id: string,
): Promise<ConversationSummary> {
  const { data } = await conversationApi.patch<ConversationSummary>(
    `/conversations/${id}/archive`,
  );
  return data;
}

export async function renameConversation(
  id: string,
  title: string,
): Promise<ConversationSummary> {
  const { data } = await conversationApi.patch<ConversationSummary>(
    `/conversations/${id}/title`,
    { title },
  );
  return data;
}

/**
 * Delete the caller's own chat for good (XC-RETENTION-LIMIT): its messages, and AskWingo's working copies of
 * its answers, in ONE call: the server deletes it in one transaction, and anyone else's chat reads 404. Files
 * he kept in the chat are his documents and stay (coordinator ruling 1): nothing here deletes a file, and
 * every failure is passed on unchanged.
 */
export async function deleteConversation(id: string): Promise<ConversationDeleted> {
  const { data } = await conversationApi.delete<ConversationDeleted>(`/conversations/${id}`);
  return data;
}

/** How many of his files kept in this chat, and made by it, stay when he deletes it (what the confirm says). */
export async function getFilesThatStay(id: string): Promise<FilesThatStay> {
  const { data } = await conversationApi.get<{
    conversation_id: string;
    kept_files: number;
    made_files?: number;
  }>(`/conversations/${id}/kept-files`);
  return { kept: data.kept_files, made: data.made_files ?? 0 };
}
